import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, Typography, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { BadgeOutlined } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { studentsApi } from '../../services/sessions/studentsApi';
import settingsService from '../../services/settingsService';
import { Group } from '../../types';
import PersonPhotoInput from '../people/PersonPhotoInput';
import { personPhotoService } from '../../services/personPhotoService';

interface EditStudentIdentityDialogProps {
  open: boolean;
  student: any | null;     // legacy Student doc shape
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  name: string;
  fatherName: string;
  fatherPhone: string;
  phone: string;
  address: string;
  contact: string;
  groupId: string;
}

const schema = yup.object({
  name:        yup.string().trim().required('Required').max(120),
  fatherName:  yup.string().trim().required('Required').max(120),
  fatherPhone: yup.string().trim().required('Required').max(40),
  phone:       yup.string().trim().required('Required').max(40),
  address:     yup.string().trim().required('Required').max(300),
  contact:     yup.string().trim().max(40),
  groupId:     yup.string()
});

const EditStudentIdentityDialog: React.FC<EditStudentIdentityDialogProps> = ({
  open, student, onClose, onSaved
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [groups, setGroups] = useState<Group[]>([]);
  // A new photo to save, null to remove the current one, undefined = unchanged.
  const [photo, setPhoto] = useState<File | null | undefined>(undefined);
  useEffect(() => { if (open) setPhoto(undefined); }, [open]);

  // Load groups on open. List endpoint returns active groups for the school.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await settingsService.getGroups({ isActive: true, limit: 200 });
        if (!cancelled) setGroups(res?.data?.groups || []);
      } catch { /* silent — picker shows empty list */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // The student doc may carry `groupId` as a string OR a populated `{ _id, name }`
  // depending on the endpoint. Normalize to the string ID for the form.
  const initialGroupId = useMemo(() => {
    const g = student?.groupId;
    if (!g) return '';
    return typeof g === 'string' ? g : String(g._id || '');
  }, [student]);

  const initialValues = useMemo<FormValues>(() => ({
    name:        student?.name ?? '',
    fatherName:  student?.fatherName ?? '',
    fatherPhone: student?.fatherPhone ?? '',
    phone:       student?.phone ?? '',
    address:     student?.address ?? '',
    contact:     student?.contact ?? '',
    groupId:     initialGroupId
  }), [student, initialGroupId]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!student?._id) return;
      try {
        await studentsApi.updateIdentity(String(student._id), {
          name: values.name.trim(),
          fatherName: values.fatherName.trim(),
          fatherPhone: values.fatherPhone.trim(),
          phone: values.phone.trim(),
          address: values.address.trim(),
          contact: values.contact.trim() || undefined,
          groupId: values.groupId || null
        });
        // The photo is the person's, so it is the same one admissions saw.
        const owner = { student: String(student._id) };
        if (photo) await personPhotoService.upload(owner, photo);
        else if (photo === null) await personPhotoService.remove(owner);
        enqueueSnackbar('Student updated', { variant: 'success' });
        onSaved();
        onClose();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not update student', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  if (!student) return null;

  return (
    <Dialog open={open} onClose={formik.isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <BadgeOutlined color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={600}>Edit student identity</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Identity edits only — class, section, and roll number are managed from the enrollment.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {student.personId && (
              <PersonPhotoInput
                label="Profile photo"
                name={formik.values.name}
                value={photo}
                currentFilename={student.photoFilename}
                onChange={setPhoto}
                disabled={formik.isSubmitting}
              />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                name="name" label="Full name" fullWidth autoFocus
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                name="phone" label="Student phone" fullWidth
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                name="fatherName" label="Father's name" fullWidth
                value={formik.values.fatherName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.fatherName && Boolean(formik.errors.fatherName)}
                helperText={formik.touched.fatherName && formik.errors.fatherName}
              />
              <TextField
                name="fatherPhone" label="Father's phone" fullWidth
                value={formik.values.fatherPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.fatherPhone && Boolean(formik.errors.fatherPhone)}
                helperText={formik.touched.fatherPhone && formik.errors.fatherPhone}
              />
            </Stack>
            <TextField
              name="address" label="Address" multiline minRows={2} fullWidth
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
            <TextField
              name="contact" label="Additional contact (optional)" fullWidth
              value={formik.values.contact}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.contact && Boolean(formik.errors.contact)}
              helperText={formik.touched.contact && formik.errors.contact}
            />
            <FormControl fullWidth>
              <InputLabel>Group (optional)</InputLabel>
              <Select
                name="groupId" label="Group (optional)"
                value={formik.values.groupId}
                onChange={formik.handleChange}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {groups.map(g => (
                  <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disableElevation disabled={formik.isSubmitting}>
            {formik.isSubmitting ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditStudentIdentityDialog;
